import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { useConsultation } from "@/components/ConsultationProvider";
import { useContent } from "@/context/ContentProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Gallery() {
  const [lightbox, setLightbox] = useState(null);
  const [pairs, setPairs] = useState([]);
  const { open } = useConsultation();
  const { gallery } = useContent();

  useEffect(() => {
    fetch(`${API}/before-after`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPairs)
      .catch(() => {});
  }, []);

  const items = gallery;

  return (
    <div className="pt-16">
      <Seo
        title="Epoxy Floor Gallery | Crownvera Epoxy Cleveland, OH"
        description="Browse Crownvera Epoxy projects: metallic floors, garage floor coatings, decorative flake systems, and polished concrete across Cleveland, Ohio."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-20 pb-24" data-testid="gallery-page">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-700 mb-4">Portfolio</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-stone-900 leading-tight">
            The <span className="text-gold-shimmer italic">Gallery</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base text-stone-600 leading-relaxed">
            A selection of Crownvera finishes. Every metallic floor is a one-of-one pour — yours will never exist twice.
          </p>
        </Reveal>

        {pairs.length > 0 && (
          <>
            <Reveal className="mt-16">
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-stone-900">Before &amp; After</h2>
              <p className="mt-2 text-sm text-stone-500">Drag the handle to compare the transformation.</p>
            </Reveal>
            <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="before-after-section">
              {pairs.map((p) => (
                <Reveal key={p.id}>
                  <BeforeAfterSlider
                    beforeUrl={`${BACKEND}${p.before_url}`}
                    afterUrl={`${BACKEND}${p.after_url}`}
                    title={p.title}
                  />
                </Reveal>
              ))}
            </div>
          </>
        )}

        <motion.div layout className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((g) => (
              <motion.button
                layout
                key={g.title}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4 }}
                onClick={() => setLightbox(g)}
                data-testid={`gallery-item-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                className="group relative rounded-lg overflow-hidden border border-amber-600/15 shadow-sm text-left"
              >
                <img src={g.url} alt={g.title} loading="lazy" className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-108" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <p className="text-xs text-amber-300 uppercase tracking-wider">{g.category}</p>
                  <p className="text-sm text-white mt-1">{g.title}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal className="mt-16 text-center">
          <p className="text-stone-600 mb-6">Want a floor like one of these? Show us the photo at your free consultation.</p>
          <button
            onClick={open}
            data-testid="gallery-cta-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-10 py-4 rounded-md shadow-md transition-colors duration-300"
          >
            Schedule Your In-Home Consultation
          </button>
        </Reveal>
      </section>

      <AnimatePresence>
        {lightbox && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-10" data-testid="gallery-lightbox">
            <motion.div className="absolute inset-0 bg-stone-900/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox(null)} />
            <motion.div
              className="relative max-w-4xl w-full"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <img src={lightbox.url} alt={lightbox.title} className="w-full max-h-[75vh] object-cover rounded-lg border border-amber-600/25 shadow-2xl" />
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-amber-300 uppercase tracking-wider">{lightbox.category}</p>
                  <p className="text-lg text-white mt-1">{lightbox.title}</p>
                </div>
                <button onClick={() => setLightbox(null)} aria-label="Close" data-testid="gallery-lightbox-close" className="text-white/70 hover:text-amber-300 transition-colors duration-200">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
